import { Quote, ShieldCheck, PhoneCall, Award, ClipboardList, Headset, MonitorCog } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { scrollToSection } from "@/lib/scroll";

const ROLE_ICONS = {
  director: Award,
  manager: ClipboardList,
  "vma-1": Headset,
  "vma-2": Headset,
  "vma-3": Headset,
  coordinator: MonitorCog,
};

const TEAM = [
  {
    id: "director",
    photo: "/director-new.jpeg",
    alt: "Karan Verma, Director of Verma Med Associates",
    name: "Karan Verma",
    role: "Director",
    desc: "Leads operations and personally reviews every VMA placement.",
  },
  {
    id: "manager",
    photo: "/manager.jpeg",
    alt: "Palak Thakur, Manager of Verma Med Associates",
    name: "Palak Thakur",
    role: "Manager",
    desc: "Runs day-to-day client success, onboarding, and quality audits.",
  },
  {
    id: "vma-1",
    photo: "/vma-1.jpeg",
    alt: "Devesh Verma, Virtual Medical Assistant at Verma Med Associates",
    name: "Devesh Verma",
    role: "Virtual Medical Assistant",
    desc: "HIPAA-trained VMA handling prior auths, scheduling, and patient calls.",
  },
  {
    id: "vma-2",
    photo: "/vma-2.jpeg",
    alt: "Sanjay Chauhan, Virtual Medical Assistant at Verma Med Associates",
    name: "Sanjay Chauhan",
    role: "Virtual Medical Assistant",
    desc: "HIPAA-trained VMA handling referrals, lab orders, and fax management.",
  },
  {
    id: "vma-3",
    photo: "/vma-3.jpeg",
    alt: "Lovlesh Verma, Virtual Medical Assistant at Verma Med Associates",
    name: "Lovlesh Verma",
    role: "Virtual Medical Assistant",
    desc: "HIPAA-trained VMA handling voicemail triage and infusion prior auths.",
  },
  {
    id: "coordinator",
    photo: "/coordinator.jpeg",
    alt: "Karan Veer, Coordinator and Technical Consultant at Verma Med Associates",
    name: "Karan Veer",
    role: "Coordinator / Technical Consultant",
    desc: "Handles site management and leads IT.",
  },
];

export const Director = () => (
  <section id="director" className="py-20 lg:py-28" data-testid="director-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid items-center gap-12 lg:grid-cols-12">
        <Reveal className="lg:col-span-5">
          <div className="relative mx-auto max-w-sm">
            <div className="pointer-events-none absolute -top-6 -left-6 h-28 w-28 rounded-[2rem] bg-[#E6F4EE]" />
            <div className="relative overflow-hidden rounded-[2rem] border-4 border-white shadow-2xl">
              <img
                src="/director-new.jpeg"
                alt="Karan Verma, Director of Verma Med Associates"
                className="h-[420px] w-full object-cover object-top"
                data-testid="director-photo"
              />
            </div>
            <div className="absolute -bottom-6 -right-4 sm:-right-8 rounded-2xl bg-[#0A261E] px-5 py-4 shadow-2xl" data-testid="director-badge">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#A3B8B0]">Verma Med Associates</p>
              <p className="mt-1 font-display text-lg font-bold text-[#F5F7F6]">Karan Verma · Director</p>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.15} className="lg:col-span-7">
          <Eyebrow>Leadership</Eyebrow>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight tracking-tight text-[#0F1E19]">
            Led personally. Accountable always.
          </h2>
          <div className="mt-8 flex gap-4">
            <Quote className="h-8 w-8 shrink-0 text-[#EAB308]" />
            <blockquote className="text-lg sm:text-xl leading-relaxed text-[#0F1E19] font-medium" data-testid="director-quote">
              "Every practice we serve gets my personal attention. Our promise is simple — reliable,
              HIPAA-trained virtual medical assistants, onboarded in 48 hours, at a price any US clinic can
              afford. If we ever fall short, you call me directly."
            </blockquote>
          </div>
          <p className="mt-6 font-mono text-sm text-[#4A5D55]">— Karan Verma, Director, Verma Med Associates</p>
          <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3">
            <span className="inline-flex items-center gap-1.5 text-sm font-medium text-[#4A5D55]">
              <ShieldCheck className="h-4 w-4 text-[#059669]" />
              Personally reviews every VMA placement
            </span>
            <span className="inline-flex items-center gap-1.5 text-sm font-medium text-[#4A5D55]">
              <PhoneCall className="h-4 w-4 text-[#059669]" />
              Direct line for every client practice
            </span>
          </div>
          <button
            data-testid="director-consult-btn"
            onClick={() => scrollToSection("#contact")}
            className="mt-10 inline-flex items-center gap-2 rounded-full bg-[#0A3B2C] px-7 py-3.5 text-sm font-semibold text-[#FAF9F5] transition-all hover:bg-[#072B20] hover:shadow-xl"
          >
            Book a Call With Our Team
          </button>
        </Reveal>
      </div>

      <Reveal delay={0.1}>
        <div className="mt-20 border-t border-[#0F1E19]/10 pt-14" data-testid="team-row">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <Eyebrow>Our Team</Eyebrow>
              <h3 className="mt-3 font-display text-2xl sm:text-3xl font-bold tracking-tight text-[#0F1E19]">
                The people behind your practice's back office.
              </h3>
            </div>
            <p className="max-w-sm text-sm leading-relaxed text-[#4A5D55]">
              A hands-on leadership team that onboards, trains, and audits every virtual medical assistant we place.
            </p>
          </div>

          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
            {TEAM.map((m) => {
              const RoleIcon = ROLE_ICONS[m.id];
              return (
              <div
                key={m.id}
                className="group rounded-3xl border border-[#E2E0D5] bg-white p-5 transition-all duration-300 hover:-translate-y-1.5 hover:shadow-xl"
                data-testid={`team-member-${m.id}`}
              >
                <div className="relative overflow-hidden rounded-2xl">
                  <img
                    src={m.photo}
                    alt={m.alt}
                    className="h-64 w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                  <span
                    className="absolute bottom-3 left-3 inline-flex items-center gap-1.5 rounded-full bg-[#0A261E]/90 px-3.5 py-1.5 font-display text-[11px] font-semibold tracking-wide text-[#EAB308] shadow-lg backdrop-blur-sm"
                    data-testid={`team-role-${m.id}`}
                  >
                    <RoleIcon className="h-3.5 w-3.5" />
                    {m.role}
                  </span>
                </div>
                <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.22em] text-[#4A5D55]">Verma Med Associates</p>
                <p className="mt-1 font-display text-lg font-bold text-[#0F1E19]" data-testid={`team-name-${m.id}`}>{m.name}</p>
                <p className="mt-1.5 text-xs leading-relaxed text-[#4A5D55]">{m.desc}</p>
              </div>
              );
            })}
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);
